/**
 * THE LINK SCHEMA. One table, read by both clients and by the build.
 *
 * Every user-facing Flashka URL has exactly one row here, and the row is what
 * makes a link mean the same thing everywhere:
 *
 *   - flashka-web routes it (the patterns mirror `flashka-web/src/App.tsx`);
 *   - flashka-app resolves an incoming deep link through it
 *     (`flashka-app/src/lib/deep-links`);
 *   - the association files claim exactly the rows marked `native`
 *     (`association.ts`), so iOS/Android never hand the app a URL it has no
 *     screen for;
 *   - the product doc's link table is generated from the same rows
 *     (`.claude/skills/product-docs/flows/deep-links.md`).
 *
 * A row with `surface: 'web-only'` is a deliberate statement, not an omission:
 * the mobile app has no screen for it, so the link stays in the browser where
 * it works. Promoting one to `native` is a product change — build the screen,
 * flip the row, and both the claims and the docs follow.
 */

export type LinkSurface = 'native' | 'web-only';

/**
 * The LEGACY deck-share URL: `/deck/<name>-share-01234`.
 *
 * Mobile's share sheet still mints these (`ShareDeckModal`), and web serves
 * them from its PUBLIC `/deck/:deckId` route by resolving the snapshot behind
 * the name. They therefore look exactly like a deck link and are not one — the
 * recipient has no such deck — so the app must never take them, and iOS is told
 * so explicitly via {@link LEGACY_DECK_SHARE_CLAIM}. Android has no exclusions,
 * which is why the parser rejects the shape as well.
 *
 * Five digits, anchored: `create-new-deck-share.ts` builds the suffix from
 * `String(Math.floor(Math.random() * 100_000_000)).slice(1, 6)`.
 */
export const LEGACY_DECK_SHARE_RE = /-share-\d{5}$/;

/** The same shape as an Apple app-site-association glob (`?` = one character). */
export const LEGACY_DECK_SHARE_CLAIM = '/deck/*-share-?????';

/**
 * What the MOBILE app needs before it can act on the link — the gate holds a
 * `required` link through sign-in rather than dropping it. Web can be more
 * permissive on the same row: `/d/<code>` previews a deck logged-out there,
 * while the app has nothing to show until it can import into a library.
 */
export type LinkAuth = 'required' | 'public';

export interface LinkRoute {
  /** Stable id. The app's href mapper and the docs both key off it. */
  readonly kind: string;
  /**
   * Web path pattern, `:name` for a dynamic segment. Matching is FIRST-WINS in
   * table order, so a static sibling (`/profile/find-mates`) must be listed
   * above the dynamic one that would also swallow it (`/profile/:locator`).
   */
  readonly web: string;
  readonly surface: LinkSurface;
  readonly auth: LinkAuth;
  /**
   * Query keys that carry meaning for this route. Anything listed here survives
   * `parseFlashkaLink` → `buildWebLink` round-trips and is handed to the app;
   * everything else (analytics noise, `utm_*`) is dropped at the boundary so a
   * shared link and a clean link resolve to the same screen.
   */
  readonly carry?: readonly string[];
  /** One line for the docs table and for the AASA `comment` field. */
  readonly summary: string;
}

/**
 * Ordered, first-match-wins. Keep static patterns above the dynamic patterns
 * that could also match them, and longer patterns above their prefixes.
 */
export const LINK_ROUTES = [
  // --- Library / decks -----------------------------------------------------
  {
    kind: 'dashboard',
    web: '/dashboard',
    surface: 'native',
    auth: 'required',
    carry: ['folder'],
    summary: 'Deck & folder browser; `?folder=` drills into one folder',
  },
  // `:deckId` is a LOCATOR: the deck's id, or its legacy path. Build links with
  // the ID — a path is a user-editable name and is not unique, so a link on one
  // breaks on a rename and can resolve to the wrong deck. Both clients accept
  // both; `@flashka/shared/deck/deck-locator` is the one resolver they share.
  {
    kind: 'deck',
    web: '/deck/:deckId',
    surface: 'native',
    auth: 'required',
    carry: ['view', 'tab', 'flashcardId'],
    summary:
      'A deck, by id (preferred) or legacy name; `?view=quizzes` lands its Quizzes list',
  },
  {
    kind: 'archive',
    web: '/archived',
    surface: 'native',
    auth: 'required',
    summary: 'Archived decks',
  },

  // --- Study runners -------------------------------------------------------
  {
    kind: 'memoAi',
    web: '/memo-ai',
    surface: 'native',
    auth: 'required',
    carry: ['deck', 'folder', 'sgFolder', 'groupId'],
    summary: 'Memo AI study session over a deck (`?deck=`) or folder (`?folder=`)',
  },
  {
    kind: 'quizRetake',
    web: '/quiz/:deckId/retake/:quizId',
    surface: 'native',
    auth: 'required',
    summary: 'Retake a finished quiz',
  },
  {
    kind: 'quizResult',
    web: '/quiz/:deckId/:quizId',
    surface: 'native',
    auth: 'required',
    summary: 'A finished quiz’s solution view',
  },
  {
    kind: 'quiz',
    web: '/quiz/:deckId',
    surface: 'native',
    auth: 'required',
    // The keys flashka-web actually mints (`study-utils.ts` → `quiz.tsx`), not
    // the mobile screen's param names — this table describes the LINK, and the
    // app translates. Getting that backwards is how a shared group-exam link
    // arrives on a phone as an ungrouped practice quiz.
    carry: ['folder', 'groupId', 'quizMode', 'configure', 'returnTo', 'originalTs'],
    summary: 'Quiz runner for a deck (or a folder, with `?folder=`)',
  },
  {
    kind: 'oralExamResult',
    web: '/oral-exam/:deckId/results/:quizTs',
    surface: 'native',
    auth: 'required',
    summary: 'A saved oral-exam report',
  },
  {
    kind: 'oralExam',
    web: '/oral-exam/:deckId',
    surface: 'native',
    auth: 'required',
    carry: ['studyGroupId'],
    summary: 'Oral exam setup for a deck',
  },
  {
    kind: 'caseStudyResult',
    web: '/case-study/:deckId/results/:quizTs',
    surface: 'native',
    auth: 'required',
    summary: 'A saved case-study report',
  },
  {
    kind: 'caseStudy',
    web: '/case-study/:deckId',
    surface: 'native',
    auth: 'required',
    carry: ['studyGroupId'],
    summary: 'Case study setup for a deck',
  },

  // --- Study groups --------------------------------------------------------
  // Not a screen: opening this JOINS the group and lands on it. Public on web
  // (the page shows a sign-in CTA and resumes the join afterwards), but
  // `required` here because joining needs an account either way — the mobile
  // gate holds the invite until sign-in rather than dropping it.
  {
    kind: 'studyGroupInvite',
    web: '/study-groups/invite/:groupId',
    surface: 'native',
    auth: 'required',
    carry: ['token'],
    summary: 'Join-by-invite link — validates the token, joins, opens the group',
  },
  {
    kind: 'studyGroupDeck',
    web: '/study-groups/:groupId/deck/:deckId',
    surface: 'native',
    auth: 'required',
    summary: 'A deck as it exists inside a study group',
  },
  {
    kind: 'studyGroup',
    web: '/study-groups/:groupId',
    surface: 'native',
    auth: 'required',
    carry: ['folder'],
    summary: 'A study group; `?folder=` drills into one of its folders',
  },
  {
    kind: 'studyGroups',
    web: '/study-groups',
    surface: 'native',
    auth: 'required',
    carry: ['create'],
    summary: 'The list of groups you belong to; `?create=1` opens Create group over it',
  },

  // --- Social --------------------------------------------------------------
  {
    kind: 'findMates',
    web: '/profile/find-mates',
    surface: 'native',
    auth: 'required',
    carry: ['q', 'src'],
    summary: 'People search; `?q=` pre-fills it',
  },
  {
    kind: 'profileSelf',
    web: '/profile',
    surface: 'native',
    auth: 'required',
    summary: 'Your own profile',
  },
  {
    kind: 'profile',
    web: '/profile/:locator',
    surface: 'native',
    auth: 'required',
    carry: ['ref', 'ch', 'lang', 'socialSource'],
    summary: 'Someone’s public profile — `@handle` or a user id',
  },

  // --- Assistant & settings ------------------------------------------------
  {
    kind: 'chatConversation',
    web: '/chat/:conversationId',
    surface: 'native',
    auth: 'required',
    summary: 'Professor Ka, one conversation',
  },
  {
    kind: 'chat',
    web: '/chat',
    surface: 'native',
    auth: 'required',
    summary: 'Professor Ka',
  },
  {
    kind: 'settings',
    web: '/settings',
    surface: 'native',
    auth: 'required',
    carry: ['tab'],
    summary: 'Settings',
  },

  // --- Shares, creation, history -------------------------------------------
  // The links people actually paste to each other. Listed rather than left to
  // the unmatched fallback so the app recognises them; the two that stay
  // `web-only` are excluded from the app-link claims and open in an in-app
  // browser instead of dead-ending on a not-found screen.
  {
    kind: 'sharedDeck',
    web: '/d/:code',
    surface: 'native',
    auth: 'required',
    summary: 'Public deck share — imports a personal copy and opens it',
  },
  {
    kind: 'sharedQuiz',
    web: '/q/:code',
    surface: 'web-only',
    auth: 'public',
    summary: 'Public quiz share',
  },
  {
    kind: 'redeem',
    web: '/redeem/:code',
    surface: 'web-only',
    auth: 'public',
    summary: 'Partner code redemption',
  },
  {
    kind: 'add',
    web: '/add',
    surface: 'native',
    auth: 'required',
    carry: ['mode', 'folder', 'youtube'],
    summary: 'Create a deck from uploaded material',
  },
  // Stays `web-only` until mobile HAS an all-quizzes surface. Not a porting
  // job: the app's only quiz list is the deck screen's, wired to that screen's
  // store, and web's dashboard carries stats and streak widgets that mobile
  // already puts on Home — so what the mobile screen should contain is a
  // product decision, not a mechanical port.
  {
    kind: 'quizzes',
    web: '/quizzes',
    surface: 'web-only',
    auth: 'required',
    summary: 'Quiz dashboard (all quizzes across decks)',
  },
  // The Biblioteque: the app has the same screens (`app/biblioteque`).
  {
    kind: 'library',
    web: '/biblioteque',
    surface: 'native',
    auth: 'required',
    summary: 'The Biblioteque: official liceo textbooks, by path, year and name',
  },
  {
    kind: 'libraryBook',
    web: '/biblioteque/:bookPath/:bookName',
    surface: 'native',
    auth: 'required',
    summary: 'One Biblioteque book, laid out like a deck: create, import or download',
  },
  {
    kind: 'libraryUnit',
    web: '/biblioteque/:bookPath/:bookName/:unitId',
    surface: 'native',
    auth: 'required',
    summary: 'The same book page, with one chapter or test open',
  },
  {
    kind: 'upgrade',
    web: '/upgrade',
    surface: 'web-only',
    auth: 'required',
    summary: 'Plan comparison / checkout',
  },
] as const satisfies readonly LinkRoute[];

export type LinkKind = (typeof LINK_ROUTES)[number]['kind'];

/** Kinds the mobile app owns a screen for — the exhaustiveness key for its href map. */
export type NativeLinkKind = Extract<
  (typeof LINK_ROUTES)[number],
  { surface: 'native' }
>['kind'];

const BY_KIND = new Map<string, LinkRoute>(LINK_ROUTES.map((r) => [r.kind, r]));

export function linkRoute(kind: LinkKind): LinkRoute {
  const route = BY_KIND.get(kind);
  // Unreachable through `LinkKind`, but the table is also read by generated
  // tooling that passes plain strings.
  if (!route) throw new Error(`[deep-links] unknown link kind: ${kind}`);
  return route;
}

/**
 * The URL prefix a route claims from the OS, in the shape both platforms want:
 * a literal path for a static route, and everything under the first dynamic
 * segment for a parameterised one (`/deck/:deckId` → `/deck/`).
 *
 * Coarser than the pattern on purpose. iOS matches a glob and Android matches a
 * prefix; neither can express "3 segments, the middle one is `results`", so the
 * claim is the widest thing that is still unambiguous, and the app re-parses the
 * URL properly once it has it.
 */
export function linkClaimPrefix(route: LinkRoute): string {
  const dynamicAt = route.web.indexOf('/:');
  return dynamicAt === -1 ? route.web : `${route.web.slice(0, dynamicAt)}/`;
}

/** True when `inner` is swallowed by `outer`'s claim — the reason for an iOS exclusion. */
export function claimShadows(outer: string, inner: string): boolean {
  return outer.endsWith('/') ? inner.startsWith(outer) : inner === outer;
}
